import React, { useEffect, useState } from 'react';
import ConsoleShell from '../../components/admin2/ConsoleShell';
import { marketImageUrl, marketTitle } from '../../lib/marketCatalog';
import { marketDb } from '../../lib/marketDb';

// Variant display modes, for review (owner, 2026-11-02 — web clarity redesign):
// how each product's variants are shown — split into attributes, folded into a
// bottom sheet, plain chips, or failed — and which ones wait for the one Gemini
// pass. Read from market_variant_attrs (admin-only beyond listed products).

const MODES = [
  ['split', 'مقسّمة إلى خصائص', 'Split'], ['folded', 'مطوية (قائمة منسدلة)', 'Folded'],
  ['failed', 'فشل التقسيم', 'Failed'], ['plain', 'خيارات قليلة', 'Plain'],
];
const PAGE = 50;

export default function ConsoleMarketVariants({ user, profile, lang }) {
  const isAr = lang === 'ar';
  const [counts, setCounts] = useState(null);
  const [mode, setMode] = useState('split');
  const [gemini, setGemini] = useState(false);
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    let alive = true;
    Promise.all([...MODES.map(([m]) => marketDb.from('market_variant_attrs').select('product_id', { count: 'exact', head: true }).eq('mode', m)),
      marketDb.from('market_variant_attrs').select('product_id', { count: 'exact', head: true }).eq('gemini_needed', true)])
      .then((rs) => {
        if (!alive) return;
        if (rs.some((r) => r.error)) { setErr(rs.find((r) => r.error).error.message); setCounts({}); return; }
        setCounts({ ...Object.fromEntries(MODES.map(([m], i) => [m, rs[i].count || 0])), gemini: rs[MODES.length].count || 0 });
      });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let alive = true;
    setRows(null);
    let q = marketDb.from('market_variant_attrs').select('product_id, mode, source, gemini_needed, parsed_at, product:market_products(id, title_ar, title_en, images)').order('parsed_at', { ascending: false }).limit(PAGE);
    q = gemini ? q.eq('gemini_needed', true) : q.eq('mode', mode);
    q.then(({ data, error }) => { if (!alive) return; if (error) setErr(error.message); setRows(data || []); });
    return () => { alive = false; };
  }, [mode, gemini]);

  const total = counts ? MODES.reduce((n, [m]) => n + (counts[m] || 0), 0) : 0;
  return (
    <ConsoleShell user={user} profile={profile} lang={lang} active="market-variants" title={isAr ? 'طريقة عرض الخيارات' : 'Variant display'}>
      <div className="ac-card" style={{ display: 'grid', gap: 10 }}>
        <p style={{ margin: 0, fontSize: 13, color: '#666' }}>
          {isAr ? 'كيف تظهر خيارات كل منتج في صفحة المنتج. التقسيم يتم بقواعد ثابتة، وما عجزت عنه القواعد ينتظر مرور Gemini مرة واحدة (بموافقة المالك).'
            : 'How each product\'s options show on its page. Rules split them; what the rules cannot split waits for one Gemini pass (owner approval).'}
        </p>
        {err && <div className="ac-placeholder" style={{ color: '#B3261E' }}>{err}</div>}
        {counts && total === 0 && !err && <div className="ac-placeholder" data-variants-empty="1">{isAr ? 'لم يُحفظ تقسيم بعد — صفحة المنتج تقسّم بالقواعد نفسها في المتصفح حتى يعمل الحفظ.' : 'Nothing stored yet — the product page applies the same rules in the browser until it is.'}</div>}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {MODES.map(([m, ar, en]) => (
            <button key={m} type="button" className={`ac-chip${!gemini && mode === m ? ' on' : ''}`} data-variant-mode={m} onClick={() => { setGemini(false); setMode(m); }}>
              {isAr ? ar : en} · <span dir="ltr">{counts ? (counts[m] || 0).toLocaleString('en-US') : '…'}</span>
            </button>
          ))}
          <button type="button" className={`ac-chip${gemini ? ' on' : ''}`} data-variant-mode="gemini" onClick={() => setGemini(true)}>
            {isAr ? 'تنتظر Gemini' : 'Waiting for Gemini'} · <span dir="ltr">{counts ? (counts.gemini || 0).toLocaleString('en-US') : '…'}</span>
          </button>
        </div>
        {rows === null ? <div className="ac-placeholder">…</div> : rows.length === 0 ? <div className="ac-placeholder">{isAr ? 'لا شيء هنا.' : 'Nothing here.'}</div> : (
          <div data-variant-rows={rows.length}>
            {rows.map((r) => {
              const img = (Array.isArray(r.product?.images) ? r.product.images : [])[0]?.ref;
              return (
                <a key={r.product_id} href={`/market/${r.product_id}`} target="_blank" rel="noreferrer" style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #f1eee8', color: 'inherit', textDecoration: 'none' }}>
                  {img ? <img src={marketImageUrl(img, true)} alt="" style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 6 }} /> : <span style={{ width: 36, height: 36, background: '#eee', borderRadius: 6 }} />}
                  <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.product ? marketTitle(r.product, lang) : r.product_id}</span>
                  <span className="ac-chip" style={{ cursor: 'default', fontSize: 11 }}>{r.source === 'gemini' ? 'Gemini' : (isAr ? 'قواعد' : 'Rules')}</span>
                </a>
              );
            })}
          </div>
        )}
      </div>
    </ConsoleShell>
  );
}
